import { useMemo, useCallback } from 'react';
import { differenceInDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AppelOffre {
  id: string;
  titre: string;
  reference: string;
  date_limite: string;
  date_publication?: string;
  date_debut_redaction_ao?: string;
  date_qr_soumissionnaires?: string;
  date_reponses_qr?: string;
  date_analyse_offres?: string;
  date_debut_soutenance?: string;
  date_fin_soutenance?: string;
  date_adjudication?: string;
  date_contractualisation?: string;
  interlocuteur_da?: string;
}

interface GanttTask {
  id: string;
  title: string;
  reference: string;
  startDate: Date;
  endDate: Date;
  type: 'redaction' | 'publication' | 'deadline' | 'qr' | 'analyse' | 'soutenance' | 'adjudication' | 'contractualisation';
  status: 'expired' | 'urgent' | 'upcoming';
  originalAO: AppelOffre;
}

type ViewMode = 'monthly' | 'weekly' | 'daily';

interface TimelineColumn {
  date: Date;
  label: string;
  fullLabel: string;
  type: 'day' | 'week' | 'month';
}

export const useGanttCalculations = (
  viewStartDate: Date,
  viewEndDate: Date,
  viewMode: ViewMode,
  columnWidth: number,
  timelineColumns: TimelineColumn[]
) => {
  // Calculate task positioning with pixel-based precision
  const getTaskPosition = useCallback((task: GanttTask) => {
    let left = 0;
    let width = 0;
    
    // Find the column indices for task start and end
    const startIndex = timelineColumns.findIndex(col => {
      if (viewMode === 'daily') {
        return col.date >= task.startDate;
      } else if (viewMode === 'weekly') {
        return col.date <= task.startDate && task.startDate < new Date(col.date.getTime() + 7 * 24 * 60 * 60 * 1000);
      } else {
        // monthly
        const month = col.date.getMonth();
        const year = col.date.getFullYear();
        return task.startDate.getMonth() === month && task.startDate.getFullYear() === year;
      }
    });
    
    const endIndex = timelineColumns.findIndex(col => {
      if (viewMode === 'daily') {
        return col.date >= task.endDate;
      } else if (viewMode === 'weekly') {
        return col.date <= task.endDate && task.endDate < new Date(col.date.getTime() + 7 * 24 * 60 * 60 * 1000);
      } else {
        // monthly
        const month = col.date.getMonth();
        const year = col.date.getFullYear();
        return task.endDate.getMonth() === month && task.endDate.getFullYear() === year;
      }
    });
    
    if (startIndex >= 0) {
      left = Math.max(0, startIndex) * columnWidth;
      const actualEndIndex = endIndex >= 0 ? endIndex : timelineColumns.length - 1;
      const columnSpan = Math.max(1, actualEndIndex - Math.max(0, startIndex) + 1);
      width = Math.max(columnSpan * columnWidth, 8); // Minimum 8px width
    }
    
    const isVisible = startIndex < timelineColumns.length && (endIndex >= 0 || startIndex >= 0);
    
    return {
      left,
      width,
      isVisible,
    };
  }, [viewStartDate, viewEndDate, columnWidth, viewMode, timelineColumns]);

  // Get color for task type and status  
  const getTaskColor = useCallback((task: GanttTask) => {
    switch (task.type) {
      case 'redaction': return 'bg-orange-500';
      case 'publication': return 'bg-orange-500';
      case 'deadline': return 'bg-orange-500';
      case 'qr': return 'bg-purple-500';
      case 'analyse': return 'bg-cyan-500';
      case 'soutenance': return 'bg-pink-500';
      case 'adjudication': return 'bg-emerald-500';
      case 'contractualisation': return 'bg-amber-600';
      default: return 'bg-primary';
    }
  }, []);

  // Format task tooltip
  const getTaskTooltip = useCallback((task: GanttTask) => {
    return `${task.title}\n${format(task.startDate, 'dd/MM/yyyy')} - ${format(task.endDate, 'dd/MM/yyyy')}`;
  }, []);

  // Calculate timeline width
  const timelineWidth = useMemo(() => {
    return timelineColumns.length * columnWidth;
  }, [timelineColumns.length, columnWidth]);

  return {
    getTaskPosition,
    getTaskColor,
    getTaskTooltip,
    timelineWidth,
  };
};